import * as pkg from '@sheet/core'
import { format, parseISO } from 'date-fns'
import {
  ANODE,
  CATHODE,
  NATURAL_GRAPHITE,
  SYNTHETIC_GRAPHITE,
} from '~/constants/services/constants'
import { getPropertyOrCategoryByProperty } from '~/constants/services/products-and-purities'
import { logError } from '~/utils/log-error'
import {
  cellObjectDate,
  cellObjectNumber,
  cellObjectText,
  cellObjectTextBold,
  getPurityHeader,
  isAnode,
  isBlackMass,
  isCathode,
  isLithium,
  isNaturalGraphite,
  isSyntheticGraphite,
} from '~/src/utils/download-xlsx-config'
import {
  getConfigByPropertyExcel,
  getConfigValuesExcel,
  getHeaderByPropertyAllPrices,
  getHeaderByPropertyExcel,
  getHeadersValuesByAllPrices,
  getHeadersValuesExcel,
  getRowsValuesExcel,
  getValueByPropertyExcel,
  getValueBySustainableGradeExcel,
} from '~/src/utils/download-xlsx-summary'
import {
  cellStylesLineBottom,
  cellStylesNormal,
  cellStylesTextCenter,
  cellStylesTitle,
  fontStyleDefault,
} from '~/src/constants/download-xlsx-config'
import type { TGeneral, TRecord } from '~/src/types/common'
import type {
  TPriceDataProcess,
  TPriceDataRawProcess,
  TPriceRaw,
  TPriceSeriesItem,
  TPriceSeriesItemProcess,
} from '~/src/types/prices-chart'
import type { WorkBook } from '@sheet/core'
import {
  getHasGrade,
  getHasMeshSize,
  getHasPayableAndPrice,
  getHasSustainableGrade,
  getHasValueByProperty,
  getLengthMarket,
} from '~/src/utils/download-xlsx-has-values'

const { utils, write } = pkg

export const downloadBlob = (
  blob: Blob,
  startDate: string,
  endDate: string,
  title: string,
) => {
  try {
    const blobUrl = window.URL.createObjectURL(blob)
    // Create a download link then click it, this is so we can get a filename
    const link = document.createElement('a')
    link.href = blobUrl
    const endDateFormatted = format(parseISO(endDate), 'yyyy-MM-dd')
    const startDateFormatted = format(parseISO(startDate), 'yyyy-MM-dd')
    link.download = `benchmark-${title
      .toLowerCase()
      .replaceAll(
        ' ',
        '-',
      )}-prices-${startDateFormatted}-${endDateFormatted}-${new Date().getTime()}.xlsx`
    link.click()
    link.remove()
    window.URL.revokeObjectURL(blobUrl)
  } catch (e) {
    logError(e)
  }
}

/**
 * extracts the properties for the headers
 */
const extractHeaderPropertiesServicePro = (
  category?: string,
  group?: TGeneral,
) => {
  const groupKey = (group.category?.name ?? group?.feedstock?.name) || ''
  const regionName = group.shippingRoute?.name || ''
  const unit = group.priceType?.name === 'Payable' ? 'Payable' : 'Price'
  const currencyUnitMeasure = group.unit === 'Percentage' ? '%' : group.unit
  const priceType = group.priceType?.name ?? ''

  const isLithiumCategory = isLithium(category)
  const productName = isLithiumCategory
    ? groupKey
    : getPropertyOrCategoryByProperty(groupKey, 'name', 'alias')
  const formula = isLithiumCategory
    ? getPropertyOrCategoryByProperty(groupKey, 'name', 'alias')
    : groupKey
  const onlyFormula = formula.includes('NiSO4') ? 'NiSO4' : formula
  let purity = group.purity?.name || ''
  let grade = group.grade || ''
  if (isAnode(category)) {
    grade += `${purity}`.trim()
  }
  if (isCathode(category)) {
    purity = groupKey
  }
  if (isNaturalGraphite(category)) {
    grade = group.meshSize?.size || ''
  }
  if (isBlackMass(category)) {
    purity = group.product.name
  }
  return {
    groupKey,
    regionName,
    productName,
    onlyFormula,
    purity,
    grade,
    unit,
    currencyUnitMeasure,
    priceType,
  }
}

/**
 * Generates the text for the Price Grade field
 */
const priceGradeGeneratorServicePro = (
  category?: string,
  seriesRow?: TGeneral,
) => {
  const { purity, regionName, productName, onlyFormula, grade, groupKey } =
    extractHeaderPropertiesServicePro(category, seriesRow)
  let complement = ''
  if (groupKey.includes(SYNTHETIC_GRAPHITE.name)) {
    complement = ` ${SYNTHETIC_GRAPHITE.name}`
  }
  let priceGradeText = 'Benchmark'
  switch (category) {
    case NATURAL_GRAPHITE.category:
      priceGradeText = `${priceGradeText} ${purity}, ${grade} ${regionName}`
      break
    case SYNTHETIC_GRAPHITE.category:
      priceGradeText = `${priceGradeText} ${productName?.toLowerCase()} ${purity} ${regionName}`
      break
    case ANODE.category:
      priceGradeText = `${priceGradeText}${complement} ${grade}, ${purity}`
      break
    case CATHODE.category:
      priceGradeText = `${priceGradeText} ${purity} ${regionName}`
      break
    default:
      priceGradeText = `${priceGradeText} ${productName?.toLowerCase()}, ${regionName}, ${onlyFormula} ${purity} ${grade}`
      break
  }
  return priceGradeText.trim()
}

/**
 * Generates the text for the purity field.
 */
const purityTextGenerator = (
  category?: string,
  formula?: string,
  purity?: string,
) => {
  const mineralReturnOnlyPurity: string[] = [
    NATURAL_GRAPHITE.category,
    SYNTHETIC_GRAPHITE.category,
    ANODE.category,
  ]
  if (mineralReturnOnlyPurity.includes(category)) {
    return purity
  }

  if (isCathode(category)) {
    return purity.split(', ').pop()
  }
  return formula.concat(' ').concat(purity)
}

/**
 * Generate the object with the data from the headers with the structure of SheetJS
 */
const headersGeneratorServicePro = (
  category?: string,
  seriesRow?: TGeneral,
) => {
  const { purity, regionName, onlyFormula, currencyUnitMeasure, priceType } =
    extractHeaderPropertiesServicePro(category, seriesRow)
  const copySeries = [...seriesRow.series]
  const firstDate = copySeries.shift().date
  const lastDate = copySeries.length === 0 ? firstDate : copySeries.pop().date
  const hideCurrency = currencyUnitMeasure === '%'
  return [
    {
      A: cellObjectTextBold('Start Date', cellStylesTextCenter),
      B: cellObjectDate(firstDate, cellStylesTextCenter),
    },
    {
      A: cellObjectTextBold('End Date', cellStylesTextCenter),
      B: cellObjectDate(lastDate, cellStylesTextCenter),
    },
    {
      A: cellObjectTextBold(
        hideCurrency ? '' : 'Currency',
        cellStylesTextCenter,
      ),
      B: cellObjectText(hideCurrency ? '' : 'USD', cellStylesTextCenter),
    },
    {
      A: cellObjectTextBold('Price Type', cellStylesTextCenter),
      B: cellObjectText(priceType, cellStylesTextCenter),
    },
    {
      A: cellObjectTextBold('Price Grade', cellStylesTextCenter),
      B: {
        t: 's',
        v: priceGradeGeneratorServicePro(category, seriesRow),
        s: {
          ...fontStyleDefault,
          ...cellStylesTextCenter,
        },
      },
    },
    {
      A: cellObjectTextBold('Shipping term', cellStylesTextCenter),
      B: cellObjectText(regionName, cellStylesTextCenter),
    },
    {
      A: cellObjectTextBold(getPurityHeader(category), cellStylesTextCenter),
      B: cellObjectText(
        purityTextGenerator(category, onlyFormula, purity),
        cellStylesTextCenter,
      ),
    },
    {
      A: cellObjectTextBold('Data source', cellStylesTextCenter),
      B: cellObjectText('Benchmark Mineral Intelligence', cellStylesTextCenter),
    },
    {
      A: cellObjectTextBold('Unit', cellStylesTextCenter),
      B: cellObjectText(currencyUnitMeasure, cellStylesTextCenter),
    },
    {},
    {
      A: cellObjectTextBold('Date', cellStylesTitle),
      B: cellObjectTextBold('Low', cellStylesTitle),
      C: cellObjectTextBold('High', cellStylesTitle),
      D: cellObjectTextBold('Mid', cellStylesTitle),
      E: cellObjectTextBold('Month Average', cellStylesTitle),
    },
  ]
}

/**
 * Generates an array of objects with the price data of a
 * "Price Grade" with the structure expected by Sheetjs
 */
const generatePriceRowsServicePro = (series: TGeneral[]) => {
  const lengthRow = 12
  let lastRows = lengthRow
  let rangeMonth = 0
  let monthAverageCellObject: TGeneral = {}
  return series.map((item, index) => {
    const isLastItem = index + 1 === series.length
    const nextItem = isLastItem ? item : series[index + 1]
    const nextDate = nextItem.date.substring(0, 7)
    const actualDate = item.date.substring(0, 7)
    const isMonthAverage = isLastItem || nextDate !== actualDate

    if (isMonthAverage) {
      monthAverageCellObject = {
        f: `AVERAGE(D${lastRows}:D${lastRows + rangeMonth})`,
        z: '#,##0',
        s: {
          ...fontStyleDefault,
          ...cellStylesNormal,
        },
      }
      lastRows = lengthRow + index + 1
      rangeMonth = 0
    } else {
      rangeMonth += 1
      monthAverageCellObject = {
        s: cellStylesNormal,
      }
    }
    return {
      A: cellObjectDate(item.date, cellStylesNormal),
      B: cellObjectNumber(item.low, '#,##0', cellStylesNormal),
      C: cellObjectNumber(item.high, '#,##0', cellStylesNormal),
      D: cellObjectNumber(item.mid, '#,##0', cellStylesNormal),
      E: monthAverageCellObject,
    }
  })
}

/**
 * Generates the name of the sheet dynamically with the data of the "Price Grade" group,
 * selecting the product name, the region name, the size and the purity
 */
const createNameSheetServicePro = (category?: string, seriesRow?: TGeneral) => {
  const { grade, productName, regionName, purity, unit, priceType } =
    extractHeaderPropertiesServicePro(category, seriesRow)

  let sheetName: string
  if (isNaturalGraphite(category)) {
    sheetName = `${purity}, ${grade} ${regionName}`
  } else if (isAnode(category)) {
    sheetName = `${grade}, ${purity}`
  } else if (isLithium(category)) {
    sheetName = lithiumDataSeriesNameBuilder(
      priceType,
      productName,
      regionName,
      grade,
    )
  } else if (isCathode(category)) {
    sheetName = `${purity}, ${regionName}`
  } else if (isSyntheticGraphite(category)) {
    sheetName = `${productName}, ${purity} ${regionName}`
  } else if (isBlackMass(category)) {
    const property = getPropertyOrCategoryByProperty(
      productName,
      'name',
      'alias',
    )
    const propertyString = property?.toString() || ''
    sheetName = `${propertyString} ${purity}-${regionName}`
  } else {
    const property = getPropertyOrCategoryByProperty(
      productName,
      'name',
      'alias',
    )
    const propertyString = property?.toString() || ''
    sheetName = `${propertyString} ${regionName}`
    if (grade) {
      sheetName = sheetName.concat(` (${grade})`)
    }
  }
  if (unit && priceType !== 'Daily Price Indicator') {
    sheetName = sheetName.concat(` (${unit})`)
  }

  return sheetName.replaceAll('/', '').substring(0, 31)
}

const lithiumDataSeriesNameBuilder = (
  priceType?: string,
  productName?: string,
  regionName?: string,
  grade?: string,
) => {
  const propertyAlias =
    getPropertyOrCategoryByProperty(productName, 'name', 'alias')?.toString() ??
    ''

  if (priceType === 'Daily Price Indicator') {
    return `${propertyAlias} Daily Price Indicator`.trim()
  }

  const baseName = `${propertyAlias} ${regionName}`.trim()
  return grade ? `${baseName} (${grade})` : baseName
}

export const objectToXlsxOneSheet = (
  category: string,
  rawData: TPriceRaw,
  name: string,
  firstDate: string | Date,
  lastDate: string | Date,
) => {
  const prices = getPriceDate(rawData)
  const workbook = createSheetForSections(
    prices,
    rawData,
    category,
    name,
    firstDate,
    lastDate,
  )
  return createBlobToWorkbook(workbook)
}

export const objectToXlsxInGroupsServicePro = (
  category?: string,
  rawData?: TPriceRaw,
) => {
  const prices = getPriceDateServicePRO(rawData)
  const workbook = createSheetsForGroupsServicePro(prices, rawData, category)
  return createBlobToWorkbook(workbook)
}

export const objectToXlsxInGroupsAllPrices = (
  data: TGeneral | TRecord,
  rawData: TPriceRaw,
) => {
  const workbook = utils.book_new()

  Object.entries(data).forEach(([category, items]) => {
    const rows = items.flatMap(mapDataToRows)
    const format = getPropertyOrCategoryByProperty(
      category,
      'category',
      'format',
    ) as string
    const sheetName = `Summary ${category}`
    const response = getValuesForSummaryServicePro(rawData, category)

    const sheetData = [
      [
        'Date',
        ...getHeaderByPropertyAllPrices(response.hasGrade, 'Grade'),
        ...getHeaderByPropertyAllPrices(response.hasMeshSize, 'MeshSize'),
        'Market',
        ...getHeadersValuesByAllPrices(response),
        ...getHeaderByPropertyAllPrices(response.hasProduct, 'Product'),
        ...getHeaderByPropertyAllPrices(
          response.hasShippingRoute,
          'Shipping Route',
        ),
        ...getHeaderByPropertyAllPrices(response.hasFormat, 'Format'),
        ...getHeaderByPropertyAllPrices(response.hasFeedstock, 'Feedstock'),
        ...getHeaderByPropertyAllPrices(response.hasCategory, 'Type'),
        ...getHeaderByPropertyAllPrices(response.hasPurity, 'Purity'),
        ...getHeaderByPropertyAllPrices(
          response.hasSustainableGrade,
          'Sustainable Grade',
        ),
        ...getHeaderByPropertyAllPrices(response.hasPriceType, 'Price Type'),
      ],
      ...rows.map((row) => {
        const values = getRowsValuesExcel({
          ...response,
          row,
          format,
        })
        return [
          cellObjectDate(row.date, cellStylesNormal),
          ...getValueByPropertyExcel(row, 'grade', response.hasGrade),
          ...getValueByPropertyExcel(row, 'meshSize', response.hasMeshSize),
          cellObjectText(row.mineral, cellStylesNormal),
          ...values,
          ...getValueByPropertyExcel(row, 'product', response.hasProduct),
          ...getValueByPropertyExcel(
            row,
            'shippingRegion',
            response.hasShippingRoute,
          ),
          ...getValueByPropertyExcel(row, 'format', response.hasFormat),
          ...getValueByPropertyExcel(row, 'feedstock', response.hasFeedstock),
          ...getValueByPropertyExcel(row, 'type', response.hasCategory),
          ...getValueByPropertyExcel(row, 'purity', response.hasPurity),
          ...getValueBySustainableGradeExcel(row, response.hasSustainableGrade),
          ...getValueByPropertyExcel(row, 'priceType', response.hasPriceType),
        ]
      }),
    ]

    const summarySheet = utils.aoa_to_sheet(sheetData)

    const config = getConfigValuesExcel(response)

    summarySheet['!cols'] = [
      { wpx: 120 },
      ...getConfigByPropertyExcel(response.hasGrade, 80),
      ...getConfigByPropertyExcel(response.hasMeshSize, 80),
      { wpx: response.lengthMarket },
      ...config,
      ...getConfigByPropertyExcel(response.hasProduct, 170),
      ...getConfigByPropertyExcel(response.hasShippingRoute, 170),
      ...getConfigByPropertyExcel(response.hasFormat, 100),
      ...getConfigByPropertyExcel(response.hasFeedstock, 120),
      ...getConfigByPropertyExcel(response.hasCategory, 140),
      ...getConfigByPropertyExcel(response.hasPurity, 140),
      ...getConfigByPropertyExcel(response.hasSustainableGrade, 170),
      ...getConfigByPropertyExcel(response.hasPriceType, 140),
    ]

    utils.book_append_sheet(workbook, summarySheet, sheetName)
  })
  return createBlobToWorkbook(workbook)
}

const getPriceDate = (rawData: TPriceRaw) => {
  const getSeries = (row: TPriceDataRawProcess) => {
    const series = row.series.map((s: TPriceSeriesItem) => ({
      Date: s.date,
      PriceHigh: s.valueHigh || 0,
      PriceLow: s.valueLow || 0,
      PriceMid: s.valueMid || 0,
    }))
    return { ...row, Series: series }
  }

  const prices = []

  if (rawData.y1) {
    rawData.y1.data = rawData.y1?.data?.map(getSeries) ?? []
    prices.push(...rawData.y1.data)
  }
  if (rawData.y2) {
    rawData.y2.data = rawData.y2?.data?.map(getSeries) ?? []
    prices.push(...rawData.y2.data)
  }
  return prices
}

const getPriceDateServicePRO = (rawData: TPriceRaw) => {
  try {
    const payable: TPriceDataProcess[] =
      rawData.y2?.data?.map((row) => {
        const series = row.series
          .map(
            (s) =>
              ({
                date: s.date,
                high: s.valueHigh,
                low: s.valueLow,
                mid: s.valueMid,
              }) as TPriceSeriesItemProcess,
          )
          .toSorted(sortDate)
        return {
          ...row,
          series,
          unit: rawData.y2.unitOfMeasure.name,
        }
      }) ?? []
    const prices: TPriceDataProcess[] =
      rawData.y1?.data?.map((row) => {
        const series = row.series
          .map(
            (s) =>
              ({
                date: s.date,
                high: s.valueHigh,
                low: s.valueLow,
                mid: s.valueMid,
              }) as TPriceSeriesItemProcess,
          )
          .toSorted(sortDate)
        return {
          ...row,
          series,
          unit: rawData.y1.unitOfMeasure.name,
        }
      }) ?? []
    return [...prices, ...payable].toSorted((before, after) => {
      const categoryNameBefore = before.seriesTitle.toLowerCase()
      const categoryNameAfter = after.seriesTitle.toLowerCase()
      if (categoryNameBefore < categoryNameAfter) {
        return -1
      }
      if (categoryNameBefore > categoryNameAfter) {
        return 1
      }
      return 0
    })
  } catch {
    return []
  }
}

const createNewSheet = (jsonData: unknown[]) =>
  utils.json_to_sheet(jsonData, {
    skipHeader: true,
  })

const createBlobToWorkbook = (workbook): Blob => {
  return new Blob([
    write(workbook, { type: 'array', bookType: 'xlsx', cellStyles: true }),
  ])
}

const createSheetsForGroupsServicePro = (
  pricesData: TPriceDataProcess[],
  rawData: TPriceRaw,
  category: string,
) => {
  const workbook = utils.book_new()
  addSummaryServicePro(workbook, rawData, category)
  const sheetNames = new Set()
  pricesData.forEach((_item, _index) => {
    /* Generates the objects with the information from rows 1 to 9 */
    const xlsxJSONData: TGeneral[] = headersGeneratorServicePro(category, _item)
    /* Generates the objects with the price information */
    const series = generatePriceRowsServicePro(_item.series)
    xlsxJSONData.push(...series)
    /* Generate sheet names dynamically */
    const sheetName = createNameSheetServicePro(category, _item)
    let sheetNameFinal = sheetName?.substring(0, 30)
    if (sheetNames.has(sheetNameFinal?.toLowerCase())) {
      sheetNameFinal = sheetNameFinal.substring(0, 28).concat(`_${_index}`)
    }
    sheetNames.add(sheetNameFinal?.toLowerCase())
    /* Transforms the created object into an Excel sheet */
    const newSheet = createNewSheet(xlsxJSONData)

    /* Change the height of the rows */
    newSheet['!rows'] = new Array(xlsxJSONData.length)
      .fill(null)
      .map((_, index) => ({ hpt: index === 10 ? 30 : 20 }))
    /* Change the width of the columns */
    newSheet['!cols'] = [
      { wpx: 120 },
      { wpx: 120 },
      { wpx: 120 },
      { wpx: 120 },
      { wpx: 120 },
    ]
    newSheet['!gridlines'] = false
    /* Add the created sheet to the Excel workbook */
    utils.book_append_sheet(workbook, newSheet, sheetNameFinal)
  })
  return workbook
}

const createSheetForSections = (
  pricesData,
  rawData: TPriceRaw,
  category: string,
  name: string,
  firstDate: string | Date,
  lastDate: string | Date,
) => {
  const workbook = utils.book_new()
  const workbookWithSummary = addSummaryServicePro(workbook, rawData, category)
  const categories = getCategories(pricesData)
  const dataSheet = getRowsWithValues(
    firstDate,
    lastDate,
    pricesData,
    category,
    categories,
  )
  const worksheet = createNewSheet(dataSheet)
  /* Change the height of the rows */
  worksheet['!rows'] = new Array(dataSheet.length)
    .fill(null)
    .map(() => ({ hpt: 20 }))
  /* Change the width of the columns */

  const cols = categories?.Unit || []
  worksheet['!cols'] = [
    { wpx: 120 },
    ...cols.map((_, index) => {
      return { wpx: index % 2 === 0 ? 200 : 80 }
    }),
  ]
  worksheet['!gridlines'] = false
  utils.book_append_sheet(workbookWithSummary, worksheet, name)
  return workbookWithSummary
}

const getRowsWithValues = (
  firstDate,
  lastDate,
  prices,
  category,
  categories,
) => {
  const dates = getDates(prices)
  const values = getValues(prices, dates, category)
  const rowsDate = dates?.map((_date: string) => {
    const data = []
    Object.values(values[_date])?.forEach((_item) => {
      data.push(_item[0] ?? { s: cellStylesNormal })
      data.push({ s: cellStylesNormal })
    })
    return [cellObjectDate(_date, cellStylesNormal), ...data]
  })
  return [
    [
      cellObjectTextBold('Start Date', cellStylesTextCenter),
      cellObjectDate(new Date(firstDate), cellStylesTextCenter),
    ],
    [
      cellObjectTextBold('End Date', cellStylesTextCenter),
      cellObjectDate(new Date(lastDate), cellStylesTextCenter),
    ],
    [
      cellObjectTextBold('Currency', cellStylesTextCenter),
      cellObjectText('USD', cellStylesTextCenter),
    ],
    [
      cellObjectTextBold('Price Type', cellStylesTextCenter),
      ...categories['Price Type'],
    ],
    [
      cellObjectTextBold('Price Grade', cellStylesTextCenter),
      ...categories['Price Grade'],
    ],
    [
      cellObjectTextBold('Shipping term', cellStylesTextCenter),
      ...categories['Shipping term'],
    ],
    [
      cellObjectTextBold(getPurityHeader(category), cellStylesTextCenter),
      ...categories.Purity,
    ],
    [
      cellObjectTextBold('Data source', cellStylesTextCenter),
      ...categories['Data source'],
    ],
    [
      cellObjectTextBold(
        isBlackMass(category) ? 'Feedstock' : 'Unit',
        cellStylesTextCenter,
      ),
      ...categories.Unit,
    ],
    [cellObjectTextBold('Date', cellStylesTextCenter)],
    ...rowsDate,
  ]
}

const getDates = (_prices) =>
  [
    ...new Set(
      _prices
        ?.map((_price) => _price.Series?.map((_item) => _item.Date).flat())
        ?.flat(),
    ),
  ]?.sort(
    (a: string, b: string) => new Date(a).getTime() - new Date(b).getTime(),
  )

const addSummaryServicePro = (
  workbook: WorkBook,
  prices: TPriceRaw,
  category: string,
) => {
  const response = getValuesForSummaryServicePro(prices)
  const format = getPropertyOrCategoryByProperty(
    category,
    'category',
    'format',
  ) as string
  const rows = []
  Object.keys(response.data)?.forEach((_items) => {
    response.data[_items]?.sort(sortDate)?.forEach((_item) => {
      const values = getRowsValuesExcel({
        ...response,
        row: _item,
        format,
      })
      rows.push([
        cellObjectDate(_item.date, cellStylesNormal),
        ...getValueByPropertyExcel(_item, 'grade', response.hasGrade),
        ...getValueByPropertyExcel(_item, 'meshSize', response.hasMeshSize),
        cellObjectText(_item.mineral, cellStylesNormal),
        ...values,
        ...getValueByPropertyExcel(_item, 'product', response.hasProduct),
        ...getValueByPropertyExcel(
          _item,
          'shippingRegion',
          response.hasShippingRoute,
        ),
        ...getValueByPropertyExcel(_item, 'format', response.hasFormat),
        ...getValueByPropertyExcel(_item, 'feedstock', response.hasFeedstock),
        ...getValueByPropertyExcel(_item, 'type', response.hasCategory),
        ...getValueByPropertyExcel(_item, 'purity', response.hasPurity),
        ...getValueBySustainableGradeExcel(_item, response.hasSustainableGrade),
        ...getValueByPropertyExcel(_item, 'priceType', response.hasPriceType),
      ])
    })
  })
  const headers = getHeadersValuesExcel(response)
  const summarySheet = createNewSheet([
    [
      cellObjectTextBold('Date', cellStylesLineBottom),
      ...getHeaderByPropertyExcel(response.hasGrade, 'Grade'),
      ...getHeaderByPropertyExcel(response.hasMeshSize, 'Mesh Size'),
      cellObjectTextBold('Market', cellStylesLineBottom),
      ...headers,
      ...getHeaderByPropertyExcel(response.hasProduct, 'Product'),
      ...getHeaderByPropertyExcel(response.hasShippingRoute, 'Shipping Route'),
      ...getHeaderByPropertyExcel(response.hasFormat, 'Format'),
      ...getHeaderByPropertyExcel(response.hasFeedstock, 'Feedstock'),
      ...getHeaderByPropertyExcel(response.hasCategory, 'Type'),
      ...getHeaderByPropertyExcel(response.hasPurity, 'Purity'),
      ...getHeaderByPropertyExcel(
        response.hasSustainableGrade,
        'Sustainable Grade',
      ),
      ...getHeaderByPropertyExcel(response.hasPriceType, 'Price Type'),
    ],
    ...rows,
  ])
  /* Change the height of the rows */
  summarySheet['!rows'] = new Array(rows?.length + 1)
    .fill(null)
    .map((_, index) => ({ hpt: index === 0 ? 30 : 22 }))

  const configValues = getConfigValuesExcel(response)
  /* Change the width of the columns */
  summarySheet['!cols'] = [
    { wpx: 120 },
    ...getConfigByPropertyExcel(response.hasGrade, 80),
    ...getConfigByPropertyExcel(response.hasMeshSize, 80),
    { wpx: response.lengthMarket },
    ...configValues,
    ...getConfigByPropertyExcel(response.hasProduct, 170),
    ...getConfigByPropertyExcel(response.hasShippingRoute, 170),
    ...getConfigByPropertyExcel(response.hasFormat, 100),
    ...getConfigByPropertyExcel(response.hasFeedstock, 120),
    ...getConfigByPropertyExcel(response.hasCategory, 170),
    ...getConfigByPropertyExcel(response.hasPurity, 140),
    ...getConfigByPropertyExcel(response.hasSustainableGrade, 170),
    ...getConfigByPropertyExcel(response.hasPriceType, 170),
  ]

  utils.book_append_sheet(workbook, summarySheet, 'Summary')
  return workbook
}

const labels = [
  'Price Type',
  'Price Grade',
  'Shipping term',
  'Purity',
  'Data source',
  'Unit',
]

const getCategories = (_prices) => {
  const data = {
    'Price Type': [],
    'Price Grade': [],
    'Shipping term': [],
    Purity: [],
    'Data source': [],
    Unit: [],
  }
  _prices?.forEach((_price) => {
    labels?.forEach((_label) => {
      if (_label === 'Data source') {
        data[_label].push(
          cellObjectText(
            'Benchmark Mineral Intelligence',
            cellStylesTextCenter,
          ),
        )
        data[_label].push({})
      }
      if (_label === 'Unit') {
        data[_label].push(
          cellObjectText(
            `${_price.currency.iso}/${_price.unitOfMeasure.symbol}`,
            cellStylesTextCenter,
          ),
        )
        data[_label].push({})
      }
      if (_label === 'Price Grade') {
        data[_label].push(
          cellObjectText(getPriceGradeName(_price), cellStylesTextCenter),
        )
        data[_label].push({})
      }
      if (_label === 'Price Type') {
        data[_label].push(
          cellObjectText(_price.priceType?.name || '', cellStylesTextCenter),
        )
        data[_label].push({})
      }
      if (_label === 'Shipping term') {
        data[_label].push(
          cellObjectText(
            _price.shippingRoute?.name || '',
            cellStylesTextCenter,
          ),
        )
        data[_label].push({})
      }
      if (_label === 'Purity') {
        data[_label].push(
          cellObjectText(_price.format?.name || '', cellStylesTextCenter),
        )
        data[_label].push({})
      }
    })
  })
  return data
}

const getPriceGradeName = (_price) => {
  const values = []
  if (_price.category?.name) values.push(_price.category.name)
  if (_price.shippingRoute?.name) values.push(_price.shippingRoute.name)
  if (_price.format?.name) values.push(_price.format.name)
  return `Benchmark ${values.join(', ')}`
}

const getValues = (_prices, dates, category, property = 'PriceMid') => {
  const data = []
  dates?.forEach((_date) => {
    data[_date] = []
    _prices?.forEach((_price) => {
      const index = getPriceGradeName(_price)
      data[_date][index] = []
      _price.Series?.forEach((_item) => {
        if (_item.Date === _date) {
          let value = {}
          if (_item[property]) {
            const format = getPropertyOrCategoryByProperty(
              category,
              'category',
              'format',
            ) as string
            value = cellObjectNumber(_item[property], format, cellStylesNormal)
          }
          data[_date][index].push(value)
        }
      })
    })
  })
  return data
}

function getValueOrZero(value?: number | string) {
  return value || 0
}

const getValuesForSummaryServicePro = (_data: TPriceRaw, _market?: string) => {
  const data = {}
  const properties = ['y1', 'y2']
  let hasPayable = false
  let hasPrice = false
  let hasGrade = false
  let hasMeshSize = false
  let hasProduct = false
  let hasShippingRoute = false
  let hasFormat = false
  let hasFeedstock = false
  let hasCategory = false
  let hasPurity = false
  let hasSustainableGrade = false
  let hasPriceType = false
  let lengthMarket = 100
  properties?.forEach((_property) => {
    _data[_property]?.data?.forEach((_item) => {
      lengthMarket = getLengthMarket(_item, lengthMarket)

      const market = _item?.Product?.ProductName
      if (
        (_market && market?.toLowerCase() === _market?.toLowerCase()) ||
        !_market
      ) {
        const { price, payable } = getHasPayableAndPrice(
          _item,
          hasPayable,
          hasPrice,
        )
        hasPayable = payable
        hasPrice = price
        hasGrade = getHasGrade(_item, hasGrade)
        hasMeshSize = getHasMeshSize(_item, hasMeshSize)
        hasProduct = getHasValueByProperty('product', _item, hasProduct)
        hasShippingRoute = getHasValueByProperty(
          'shippingRoute',
          _item,
          hasShippingRoute,
        )
        hasFormat = getHasValueByProperty('format', _item, hasFormat)
        hasFeedstock = getHasValueByProperty('feedstock', _item, hasFeedstock)
        hasCategory = getHasValueByProperty('category', _item, hasCategory)
        hasPurity = getHasValueByProperty('purity', _item, hasPurity)
        hasSustainableGrade = getHasSustainableGrade(_item, hasSustainableGrade)
        hasPriceType = getHasValueByProperty('priceType', _item, hasPriceType)
      }

      const idSeries = `${_item.category?.name || _item.feedstock?.name}-
      ${_item.seriesTitle}-${_item.unitOfMeasure.name}`
      data[idSeries] = []
      _item.series?.forEach(() => {
        data[idSeries] = mapDataToRows(_item)
      })
    })
  })
  return {
    data,
    hasPayable,
    hasPrice,
    hasGrade,
    hasMeshSize,
    hasProduct,
    hasShippingRoute,
    hasFormat,
    hasFeedstock,
    hasCategory,
    hasType: hasFeedstock || hasCategory,
    hasPurity,
    hasSustainableGrade,
    hasPriceType,
    lengthMarket,
  }
}

const mapDataToRows = (item) => {
  const unit = item.unitOfMeasure.name
  return item.series.map((seriesItem) => ({
    date: format(parseISO(seriesItem.date), 'yyyy-MM-dd'),
    grade: item?.grade || '',
    mineral: item.Product.ProductName || '',
    priceLow: unit !== 'Percentage' ? getValueOrZero(seriesItem?.valueLow) : 0,
    priceHigh:
      unit !== 'Percentage' ? getValueOrZero(seriesItem?.valueHigh) : 0,
    priceMid: unit !== 'Percentage' ? getValueOrZero(seriesItem?.valueMid) : 0,
    payableLow:
      unit === 'Percentage' ? getValueOrZero(seriesItem?.valueLow) : 0,
    payableHigh:
      unit === 'Percentage' ? getValueOrZero(seriesItem?.valueHigh) : 0,
    payableMid:
      unit === 'Percentage' ? getValueOrZero(seriesItem?.valueMid) : 0,
    purity: item.purity?.name || '',
    shippingRegion: item.shippingRoute?.name || '',
    type: item.category?.name || '',
    meshSize: item.meshSize?.size || '',
    feedstock: item.feedstock?.name || '',
    product: item.product?.name || '',
    format: item.format?.name || '',
    priceType: item.priceType?.name || '',
    unit,
    sustainableGrade:
      item?.category?.isSustainable || item?.feedstock?.isSustainable || false,
  }))
}

const sortDate = (a: TGeneral, b: TGeneral) =>
  new Date(a.date).getTime() - new Date(b.date).getTime()
